<template>
  <v-card
    class="blog-card-neumorphic"
    :class="{ 'blog-card-neumorphic-mobile': isMobile }"
    @click="goToDetails"
  >
    <div class="image-container">
      <!-- Image with loader -->
      <v-img :src="blog.cover" class="blog-image" transition="fade-transition">
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular
              indeterminate
              color="blue lighten-5"
            ></v-progress-circular>
          </v-row>
        </template>
      </v-img>
      <div class="category-tag">Article</div>
    </div>

    <div class="card-content">
      <div class="d-flex justify-space-between align-start">
        <h3 class="blog-title">{{ blog.title }}</h3>
        <div v-if="profile._id == blog.poster" class="action-buttons">
          <v-btn icon @click.stop="editBlog" class="edit-btn">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <!-- <v-btn icon @click.stop="onDeleteBlog" class="delete-btn">
            <v-icon >mdi mdi-trash-can-outline</v-icon>
          </v-btn> -->
        </div>
      </div>

      <p class="blog-excerpt">{{ extractContent(blog.html) }}</p>

      <div class="card-footer">
        <div class="author-info" @click.stop="goToAgencyProfile(blog.poster)">
          <editable-avatar
            :image="blog.poster_image"
            :editable="false"
            :size="$vuetify.breakpoint.xs ? 30 : 40"
          />
          <div class="author-details">
            <div class="author-name">{{ blog.poster_name }}</div>
            <div class="post-date">{{ getTimeAgo(blog.createdAt) }}</div>
          </div>
        </div>
        <v-btn icon small @click.stop="goToDetails">
          <v-icon small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>
    <confirm-delete-dialog
      :show="deleteDialog"
      :loading="loading"
      :message="'Are you sure you want to delete this blog?'"
      @close="onCloseDialog"
      @confirm="onConfirmDelete"
    />
  </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import EditableAvatar from "../../components/EditableAvatar.vue";

export default {
  components: { ConfirmDeleteDialog, EditableAvatar },
  props: {
    blog: Object
  },

  data() {
    return {
      snackbar: false,
      errorMessage: null,
      deleteDialog: false,
      loading: false
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    isMobile() {
      return this.$vuetify.breakpoint.xs;
    }
  },
  methods: {
    ...mapActions("blog", {
      deleteBlog: "deleteBlog"
    }),
    extractContent(s) {
      const span = document.createElement("span");
      span.innerHTML = s;
      const text = span.textContent || span.innerText;
      return text.length > 150 ? text.substring(0, 150) + "..." : text;
    },
    goToDetails() {
      this.$router.push({
        name: "blog-details",
        query: { _id: this.blog._id }
      });
    },
    editBlog() {
      this.$router.push({
        name: "edit-blog",
        query: { _id: this.blog._id }
      });
    },
    onDeleteBlog() {
      this.deleteDialog = true;
    },
    onCloseDialog() {
      this.deleteDialog = false;
    },
    async onConfirmDelete() {
      this.loading = true;
      try {
        await this.deleteBlog({ _id: this.blog._id });
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message || error.message || "An error occurred";
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },
    goToAgencyProfile(agency) {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: agency }
      });
    },
    getTimeAgo(oldTimeStr) {
      const oldTime = Date.parse(oldTimeStr);
      const time = new Date();
      const different = (time.getTime() - oldTime) / 1000;

      const month = Math.floor(different / 3600 / 24 / 30);
      const days = Math.floor((different / 3600 / 24) % 30);
      const hours = Math.floor((different / 3600) % 24);
      const mins = Math.floor((different / 60) % 60);

      if (month === 1) return "1 month ago";
      if (month > 1) return `${month} months ago`;
      if (days === 1) return "1 day ago";
      if (days > 1) return `${days} days ago`;
      if (hours === 1) return "1 hour ago";
      if (hours > 1) return `${hours} hours ago`;
      if (mins === 1) return "1 min ago";
      if (mins > 1) return `${mins} mins ago`;
      return "Just now";
    }
  }
};
</script>

<style scoped>
.blog-card-neumorphic {
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 8px 8px 16px #d9d9d9, -8px -8px 16px #ffffff;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.blog-card-neumorphic:hover {
  transform: translateY(-5px);
  box-shadow: 12px 12px 24px #d9d9d9, -12px -12px 24px #c0c0c0;
}

.blog-card-neumorphic-mobile {
  box-shadow: 4px 4px 8px #d9d9d9, -4px -4px 8px #ffffff;
}

.image-container {
  position: relative;
  height: 180px;
  overflow: hidden;
}

.blog-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.blog-card-neumorphic:hover .blog-image {
  transform: scale(1.05);
}

.category-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 600;
}

.card-content {
  padding: 20px;
}

.blog-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #2d3748;
}

.blog-excerpt {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-details {
  margin-left: 10px;
}

.author-name {
  font-size: 0.85rem;
  font-weight: 500;
}

.post-date {
  font-size: 0.75rem;
  color: #718096;
}

@media (max-width: 600px) {
  .blog-card-neumorphic {
    margin-bottom: 16px;
  }

  .image-container {
    height: 150px;
  }

  .card-content {
    padding: 16px;
  }

  .blog-title {
    font-size: 1.1rem;
  }
}

button.edit-btn.v-btn.v-btn--icon.v-btn--round.theme--light.v-size--default {
  color: grey;
}
button.delete-btn.v-btn.v-btn--icon.v-btn--round.theme--light.v-size--default {
  color: red;
}
</style>
